import { Exercise } from '../../../entities/exercise/exercise';
import { IExerciseOccurance } from '../../../logic/workoutselection';
import { IExerciseRepository } from '../interfaces/iexercise-repository';

interface IInMemoryCache {
  all: Exercise[];
  idMap: Map<string, Exercise>;
}

export function createInMemoryExerciseCache(): IInMemoryCache {
  return {
    all: [],
    idMap: new Map<string, Exercise>(),
  };
}

// cached for the duration of the process
// todo we need a daily cache invalidation or other strategy
const singleProcessWideCache: IInMemoryCache = {
  all: [],
  idMap: new Map<string, Exercise>(),
};
export class CachedExerciseRepository implements IExerciseRepository {

  constructor(
    private underlyingExerciseRepository: IExerciseRepository,
    private readonly cache: IInMemoryCache = singleProcessWideCache,
  ) { }

  public async createCache(): Promise<Exercise[]> {
    return this.getAll();
  }

  public async getAll(): Promise<Exercise[]> {
    if (!this.cache.all || this.cache.all.length === 0) {
        this.cache.all = await this.underlyingExerciseRepository.getAll();
        this.cache.idMap = new Map<string, Exercise>();
        for (const exs of this.cache.all) {
          const key = this.getGuaranteedToBeStringOrUndefined(exs.uniqueReferenceNumber);
          if (key != null) {
            this.cache.idMap.set(key, exs);
          }
        }
    }
    return this.cache.all;
  }

  public async resetCache(): Promise<void> {
    this.cache.all = [];
    this.cache.idMap = new Map<string, Exercise>();
    await this.createCache();
  }

  public updateCachedExerciseStats(listOfExercisesUsedByWorkouts: Map<string, IExerciseOccurance>): void {
    if (!this.cache.all) {
      return;
    }
    const updatedCache = this.cache.all.map((exercise: Exercise) => {
      const workoutExercise: IExerciseOccurance = listOfExercisesUsedByWorkouts[exercise.uniqueReferenceNumber];
      if (workoutExercise) {
        exercise.firstUsedSetNumber = workoutExercise.firstUserSetNumber;
        exercise.lastUsedSetNumber = workoutExercise.lastUsedSetNumber;
        exercise.numberOfOccurances = workoutExercise.numberOfOccurances;
      } else {
        exercise.firstUsedSetNumber = 0;
        exercise.lastUsedSetNumber = 0;
        exercise.numberOfOccurances = 0;
      }
      return exercise;
    });

    this.cache.all = updatedCache;
  }

  public async get(id: string): Promise<Exercise | undefined> {
    await this.getAll(); // ensure it's cached
     // need to use map, since a find is too slow:
    const key = this.getGuaranteedToBeStringOrUndefined(id);
    if (key != null) {
      return this.cache.idMap.get(key);
    }
    return undefined;
  }

  public async add(exercise: Exercise): Promise<void> {
    this.cache.all = [];
    await this.underlyingExerciseRepository.add(exercise);
  }

  private getGuaranteedToBeStringOrUndefined(id: string): string | undefined {
    if (id == null) {
      return undefined;
    }
    return id.toString();
  }

}
